import {Store} from "vuex";
import {AgendaService} from "@share/program/data/AgendaService";
import {Element} from "@api/models/Element";
import {EventMembership} from "@api/models/EventMemberShip";
import {AgendaInfoService} from "@share/program/data/AgendaInfoService";
import {WebinarInfo} from "@api/models/WebinarInfo";
import {Rating} from "@api/models/Rating";
import {EventInterface} from "@share/program/types/EventInterface";
import {ProgramDataMapper} from "@share/program/helpers/ProgramDataMapper";
import {ArrayHelpers} from "@common/ArrayHelpers";
import vueUpdateArray from "@common/vueUpdateArray";
import Vue from "vue";
import {ProgramDataHelper} from "@share/program/helpers/ProgramDataHelper";
import {isAgendaNotForDisplay} from "@common/AgendaHelper";

export class FavoritesController {
    isLoaded  = false;
    agendas: Element[] = [];
    speakers: Record<string, EventMembership[]> = {};
    webinarsInfo: { [key: string]: WebinarInfo } = {};
    likes: { [key: string]: Rating } = {};
    comments: Record<string, number> = {};
    preparedAgendas: EventInterface[] = [];
    loadedIds: string[] = [];

    isLoading = false;

    constructor(
        private readonly store: Store<any>,
        public readonly agendaService: AgendaService,
        private readonly agendaInfoService: AgendaInfoService
    ) {
    }

    get selectedEventId() {
        return this.store.state.events.selected
    }

    get ids(): string[] {
        return this.store.state.favorites.list[this.agendaService.schema] ?? [];
    }

    public async load() {
        if (this.isLoaded || this.ids?.length == 0 || !this.ids || !this.selectedEventId) { return }
        let ids = [...this.ids];

        this.isLoading = true;

        ids = this.searchInLoaded(ids);

        if (ids.length > 0) {
            await this.loadData(ids, false);
        }

        this.prepareAgendas();

        this.isLoading = false;

        this.isLoaded = true;
    }

    private async loadData(ids: string[] = [], runLoader = true) {
        const loadedIds = this.agendas.map(item => item.id);

        ids = ids.filter(item => !loadedIds.includes(item));

        if (ids.length == 0) { return; }

        runLoader && (this.isLoading = true);

        let agendas = await this.agendaService.fetchByIds({
            eventId: this.selectedEventId,
            ids: ids
        }, this.store.getters['groupFilter'] ?? {});

        agendas = agendas.filter(item => !loadedIds.includes(item.id));

        const agendaIds: string[] = agendas.map(item => item.id);

        const speakers = await this.agendaInfoService.fetchSpeakers(agendaIds);
        const {webinarInfos, likes, comments} = await this.agendaInfoService.fetchAdditionalData(agendaIds);
        this.addNewData(agendas, speakers, webinarInfos, likes, comments);

        // this.prepareAgendas();

        this.loadedIds = [...this.loadedIds, ...ids];

        runLoader && (this.isLoading = false);
    }

    private addNewData(
        agendas: Element[],
        speakers: Record<string, EventMembership[]>,
        webinarInfos: Record<string, WebinarInfo>,
        likes: Record<string, Rating>,
        comments: Record<string, number>
    ) {
        this.agendas = [...this.agendas, ...agendas];
        this.speakers = {...this.speakers, ...speakers};
        this.webinarsInfo = {...this.webinarsInfo, ...webinarInfos};
        this.likes = {...this.likes, ...likes};
        this.comments = {...this.comments, ...comments};
    }

    public removeAgenda(id: string) {
        this.agendas = this.agendas.filter(item => item.id != id);
        this.loadedIds = this.loadedIds.filter(item => item != id);
        this.prepareAgendas();
    }

    public async handleFaveAgendasChange() {
        if (!this.isLoaded) { return }

        await this.removeDeleted();
        await this.addNewAgendas();
        this.prepareAgendas();
    }

    private async removeDeleted() {
        const currentFavorites = this.ids;
        this.agendas = this.agendas.filter(item => currentFavorites.includes(item.id));
        this.loadedIds = this.loadedIds.filter(item => currentFavorites.includes(item));
    }

    private searchInLoaded(missingIds: string[]) {
        const programs: Record<string, ProgramDataHelper> = this.store.state.program.programs ?? {};
        for (const [dateStr, program] of Object.entries(programs)) {
            const agendas = program.todayAgendas.filter(item => missingIds.includes(item.id));

            if (agendas.length === 0) {continue;}
            const agendasIds = agendas.map(item => item.id);
            let getInfo = <T>(programKey: keyof ProgramDataHelper): Record<string, T> => {
                const newObject: Record<string, T> = {} as any;
                agendas.forEach(item => {
                    if (program[programKey][item.id]) {
                        newObject[item.id] = program[programKey][item.id];
                    }
                });
                return newObject;
            }
            const speakers = getInfo<EventMembership[]>('speakers');
            const likes: Record<string, Rating> = getInfo<Rating>('likes');
            const webinarInfos: Record<string, WebinarInfo> = getInfo<WebinarInfo>('webinarsInfo');
            const comments = getInfo<number>('comments');

            this.addNewData(agendas, speakers, webinarInfos, likes, comments);

            this.loadedIds = [...this.loadedIds, ...agendasIds];

            missingIds = missingIds.filter(item => !agendasIds.includes(item));
            if (missingIds.length == 0) {break;}
        }

        return missingIds;
    }

    private async addNewAgendas() {
        let missingIds = this.ids.filter(item => !this.loadedIds.includes(item));

        if (missingIds.length) {
            missingIds = this.searchInLoaded(missingIds);

            if (missingIds.length) {
                await this.loadData(missingIds);
            }
        }
    }

    private prepareAgendas() {
        this.preparedAgendas =
            ArrayHelpers.sortBy(this.agendas, 'beginAt')
            .map((item) =>
                ProgramDataMapper.toEventInterface(item, this.speakers, this.webinarsInfo, this.likes, this.comments)
            );
    }

    public async updateAgenda(agenda: Element) {
        if (!this.ids.includes(agenda.id)) { return; }

        let newAgendas: Element[] = [];

        if (isAgendaNotForDisplay(agenda, this.selectedEventId, this.store.getters.groupIds)) {
            newAgendas = this.agendas.filter((item) => item.id !== agenda.id);
        }
        else if (agenda.eventId === this.selectedEventId) {
            newAgendas = [agenda, ...this.agendas];
        }

        if (newAgendas && newAgendas.length == 0) { return; }

        vueUpdateArray(this.agendas, newAgendas);

        this.prepareAgendas();
    }

    public async updateLikes(agendaId: string, like: Rating) {
        if (!this.ids.includes(agendaId)) {return;}

        const oldLike = this.likes[agendaId];

        let needToUpdate = oldLike ? oldLike.count != like.count : true;
        if (!needToUpdate) { return; }

        Vue.set(this.likes, agendaId, like);
        this.prepareAgendas();
    }

    public async updateComments(agendaId: string, commentsNumber: number) {
        if (!this.ids.includes(agendaId)) {return;}

        Vue.set(this.comments, agendaId, commentsNumber);
        this.prepareAgendas();
    }
}
